// localStorage persistence for the trip planner.
//
// Key: `yourawayday_trips_v1`. It was deliberately distinct from the old
// saved-stadiums key, which held bookmarked stadium keys rather than trips;
// W5b retired that key and its heart button, so the distinction is now
// historical. These are full Trip objects from the planner.
//
// Shape: Object<tripId, Trip>. Per-trip lookups are O(1) and listing is
// the only iteration we ever do.

import { migrateTrip } from './tripSchema';

const STORAGE_KEY = 'yourawayday_trips_v1';
// The trip the visitor is CURRENTLY working on, by id.
//
// Founder decision, 2026-08-03: an explicit pointer rather than reusing
// `updated_at`. Before this, "active" was derived — the most recently updated
// trip — which is fine with one trip and wrong the moment there are two:
// opening an older trip does not UPDATE it, so a reload silently snapped back
// to the newer one, and `/trip` derived the same answer independently, so the
// planner and the organiser's own sheet could show different trips at once.
//
// The rejected alternative was to bump `updated_at` on open. That makes the
// switcher work in one line and makes a timestamp lie: the trip list is ORDERED
// by that field, so browsing your trips would reorder them, and anything later
// hung off "last updated" inherits the lie. This project does not trade a true
// field for a cheap fix.
export const ACTIVE_KEY = 'yourawayday_active_trip_v1';

const readAll = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const writeAll = (trips) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trips));
  } catch {
    /* quota or disabled storage — fail silently */
  }
};

export const loadTrip = (id) => {
  if (!id) return null;
  const all = readAll();
  const raw = all[id];
  return raw ? migrateTrip(raw) : null;
};

// The `storage` event fires in OTHER tabs only, so a tab that adds or deletes
// a trip cannot see its own change. That is not theoretical: driving W5b found
// the navbar badge still reading "2" after a trip was deleted on /my-trips in
// the same tab. This is the in-tab half of the same signal.
//
// Deliberately NOT fired on every save. `PlannerContext` writes through on
// every keystroke, and a count that only changes when a trip is ADDED or
// REMOVED has nothing to learn from an edit — broadcasting one would re-parse
// storage and re-render the navbar per character typed.
const TRIPS_CHANGED = 'yad:trips-changed';

const notifyTripsChanged = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(TRIPS_CHANGED));
};

export const saveTrip = (trip) => {
  if (!trip || !trip.id) return;
  const all = readAll();
  const isNew = !all[trip.id];
  all[trip.id] = trip;
  writeAll(all);
  if (isNew) notifyTripsChanged();
};

export const listTrips = () => {
  const all = readAll();
  return Object.values(all)
    .map(migrateTrip)
    .filter(Boolean)
    .sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
};

export const getActiveTripId = () => {
  try {
    return localStorage.getItem(ACTIVE_KEY) || null;
  } catch {
    return null;
  }
};

export const setActiveTripId = (id) => {
  try {
    if (id) localStorage.setItem(ACTIVE_KEY, id);
    else localStorage.removeItem(ACTIVE_KEY);
  } catch {
    /* quota or disabled storage — the fallback below still works */
  }
};

export const deleteTrip = (id) => {
  if (!id) return;
  const all = readAll();
  if (!(id in all)) return;
  delete all[id];
  writeAll(all);
  // A pointer at a trip that no longer exists is worse than no pointer: it
  // would send the planner down the fallback path on every load for no reason.
  if (getActiveTripId() === id) setActiveTripId(null);
  notifyTripsChanged();
};

/**
 * The trip the planner and the organiser's own sheet should both open.
 *
 * The pointer wins. It is allowed to be absent (every visitor from before this
 * shipped) or stale (a trip deleted in another tab), and in both cases we fall
 * back to the most recently updated trip — the old rule — rather than showing
 * someone an empty planner because a pointer went bad. A successful fallback
 * repairs the pointer, so the next read is a direct hit.
 */
export const loadActiveTrip = () => {
  const id = getActiveTripId();
  if (id) {
    const pointed = loadTrip(id);
    if (pointed) return pointed;
  }
  const trips = listTrips();
  const fallback = trips[0] || null;
  if (fallback && fallback.id !== id) setActiveTripId(fallback.id);
  else if (!fallback && id) setActiveTripId(null);
  return fallback;
};

/**
 * Fire `cb` when the set of trips changes — in another tab OR in this one.
 *
 * Two events, because neither covers the other. `storage` fires only in OTHER
 * tabs, which is the case a tab cannot otherwise see: without it the trip list
 * and the navbar count go stale, and worse, this tab's write-through can
 * resurrect a trip another tab deleted, because it holds the deleted trip in
 * memory and saves it back on the next keystroke. `yad:trips-changed` fires
 * only in THIS tab, and covers the case `storage` refuses to — a trip added or
 * deleted right here.
 */
export const subscribeToTripStorage = (cb) => {
  if (typeof window === 'undefined' || typeof cb !== 'function') return () => {};
  const onStorage = (e) => {
    if (!e.key || e.key === STORAGE_KEY || e.key === ACTIVE_KEY) cb(e.key);
  };
  const onLocal = () => cb(STORAGE_KEY);
  window.addEventListener('storage', onStorage);
  window.addEventListener(TRIPS_CHANGED, onLocal);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(TRIPS_CHANGED, onLocal);
  };
};
